import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useMemo } from "react";

import {
  type PieceBackfillRunFieldsFragment,
  PieceBackfillRunMode,
} from "../../generated/graphql";
import { parsePieceBackfillReport } from "./piece-backfill-report";
import PieceBackfillReportPanel from "./piece-backfill-report-view";
import { isRunInFlight, runProgressPercent } from "./piece-backfill-run-state";
import PieceBackfillStatusChip from "./piece-backfill-status-chip";

const styles = {
  root: { mt: 4 },
  header: { mb: 1.5 },
  progress: { maxWidth: 900, mb: 1 },
  alert: { maxWidth: 900, mb: 2 },
  counters: { mb: 1 },
} satisfies Record<string, SxProps<Theme>>;

/** Props for {@link PieceBackfillRunPanel}. */
export type PieceBackfillRunPanelProps = {
  /** The active run, or null before the first run of the session. */
  readonly run: PieceBackfillRunFieldsFragment | null;
  /** True once polling gave up after repeated read failures. */
  readonly pollAbandoned: boolean;
};

/**
 * Live view of the active run: progress while the worker walks its packages, the
 * error when it fails, and the full report once it finishes.
 *
 * Dry runs and executes report the same shape, so both render through the same
 * report panel; only the framing changes.
 */
const PieceBackfillRunPanel = ({
  run,
  pollAbandoned,
}: PieceBackfillRunPanelProps) => {
  const report = useMemo(
    () => parsePieceBackfillReport(run?.report),
    [run?.report],
  );
  const progressPercent = useMemo(
    () =>
      run === null
        ? null
        : runProgressPercent({
            packagesTotal: run.packagesTotal,
            packagesProcessed: run.packagesProcessed,
          }),
    [run],
  );

  if (run === null) {
    return null;
  }

  const isDryRun = run.mode === PieceBackfillRunMode.DryRun;
  const inFlight = isRunInFlight(run.status);

  return (
    <Box sx={styles.root}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={styles.header}
      >
        <Typography variant="subtitle1">
          {isDryRun ? "Dry run" : "Execute"}
        </Typography>
        <PieceBackfillStatusChip status={run.status} />
        {isDryRun ? (
          <Chip label="nothing written" size="small" variant="outlined" />
        ) : null}
      </Stack>

      {pollAbandoned ? (
        <Alert severity="warning" sx={styles.alert}>
          Lost track of this run after repeated failed reads. The run itself is
          unaffected and keeps going; reload the page or check the run history
          below for its outcome.
        </Alert>
      ) : null}

      {inFlight ? (
        <>
          <Box sx={styles.progress}>
            {progressPercent === null ? (
              <LinearProgress />
            ) : (
              <LinearProgress variant="determinate" value={progressPercent} />
            )}
          </Box>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={styles.counters}
          >
            {run.packagesTotal === 0
              ? "Planning the run…"
              : `${run.packagesProcessed} of ${run.packagesTotal} packages processed — ${run.piecesCreated} pieces created, ${run.piecesUpdated} updated, ${run.joinsCreated} package links, ${run.packagesSkipped} skipped.`}
          </Typography>
        </>
      ) : null}

      {run.error === null || run.error === undefined ? null : (
        <Alert severity="error" sx={styles.alert}>
          {run.error}
          <Typography variant="body2">
            The backfill is idempotent, so re-running after fixing the cause is
            safe: it will skip whatever the failed run already wrote.
          </Typography>
        </Alert>
      )}

      {report === null ? null : (
        <PieceBackfillReportPanel report={report} isDryRun={isDryRun} />
      )}
    </Box>
  );
};

export default PieceBackfillRunPanel;
